import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { getStockDetail } from '../api';
import PriceChart from '../components/PriceChart';
import EarningsFundamentalsCard from '../components/EarningsFundamentalsCard';

const PERIODS = [
  { key: '3mo', label: '3M' },
  { key: '6mo', label: '6M' },
  { key: '1y', label: '1Y' },
  { key: '2y', label: '2Y' },
];

const sectionStyle = {
  border: '1px solid var(--border)',
  borderRadius: 14,
  background: 'var(--surface)',
  padding: 16,
  marginTop: 12,
};

const labelStyle = { color: 'var(--muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 };

function relativeAge(isoDate) {
  if (!isoDate) return '';
  const ms = Date.now() - new Date(isoDate).getTime();
  const mins = Math.max(1, Math.floor(ms / 60000));
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatMarketCap(value) {
  if (value == null) return '—';
  const n = Number(value);
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  return `$${n.toLocaleString()}`;
}

export default function StockPage() {
  const { ticker } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // No explicit ?asset_type= means "figure it out" — the backend tries stock
  // first and falls back to fx, so e.g. a bare "USDJPY" resolves correctly
  // instead of erroring as an unrecognized stock symbol. An explicit choice
  // (from the dropdown, or a link that already knows the type) is always
  // honored as-is.
  const requestedAssetType = searchParams.get('asset_type') || 'auto';
  const [period, setPeriod] = useState('6mo');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Once a response comes back, `data.asset_type` is what actually resolved
  // (e.g. "auto" -> "fx") — use that for display/gating so the UI reflects
  // reality instead of the possibly-still-ambiguous request.
  const resolvedAssetType = data?.asset_type || requestedAssetType;

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError('');
    getStockDetail(ticker, { period, assetType: requestedAssetType })
      .then((res) => {
        if (!mounted) return;
        setData(res.data);
      })
      .catch(() => {
        if (!mounted) return;
        setData(null);
        setError('Unable to load data for this ticker right now.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => { mounted = false; };
  }, [ticker, period, requestedAssetType]);

  const priceChartData = useMemo(() => data?.price_series || [], [data]);
  const info = data?.info;
  const infoError = info?.error;

  const setAssetType = (next) => {
    const params = new URLSearchParams(searchParams);
    params.set('asset_type', next);
    setSearchParams(params);
  };

  return (
    <div style={{ padding: '26px 26px 30px', maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', justifyContent: 'space-between' }}>
        <div>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', color: '#7c8cff', cursor: 'pointer', padding: 0, fontSize: 13, marginBottom: 6 }}
          >
            ← Back
          </button>
          <h1 style={{ margin: 0, fontSize: 32 }}>{ticker.toUpperCase()}</h1>
          {!infoError && info?.name && (
            <div style={{ color: 'var(--muted)', fontSize: 14 }}>{info.name}{info.sector ? ` · ${info.sector}` : ''}</div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={resolvedAssetType}
            onChange={(e) => setAssetType(e.target.value)}
            style={{ padding: '8px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text)' }}
          >
            <option value="auto">Auto-detect</option>
            <option value="stock">Stock</option>
            <option value="fx">Forex</option>
            <option value="crypto" disabled>Crypto (coming soon)</option>
          </select>
          <div style={{ display: 'flex', gap: 4 }}>
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                style={{
                  padding: '6px 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  background: period === p.key ? 'var(--card, #1c2333)' : 'transparent',
                  color: period === p.key ? 'var(--text)' : 'var(--muted)',
                  cursor: 'pointer',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && !data && (
        <div style={{ ...sectionStyle, color: 'var(--muted)' }}>Loading {ticker.toUpperCase()}...</div>
      )}

      {!loading && error && (
        <div style={{ ...sectionStyle, color: 'var(--muted)' }}>{error}</div>
      )}

      {data && (
        <>
          <section style={sectionStyle}>
            {infoError ? (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>{infoError}</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 14 }}>
                <div>
                  <div style={labelStyle}>Price</div>
                  <div style={{ fontWeight: 700, fontSize: 20 }}>{info?.current_price != null ? `$${info.current_price}` : '—'}</div>
                </div>
                <div>
                  <div style={labelStyle}>Market Cap</div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{formatMarketCap(info?.market_cap)}</div>
                </div>
                <div>
                  <div style={labelStyle}>P/E Ratio</div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{info?.pe_ratio != null ? Number(info.pe_ratio).toFixed(2) : '—'}</div>
                </div>
                <div>
                  <div style={labelStyle}>RSI (14)</div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{info?.rsi_14 ?? '—'}</div>
                </div>
                <div>
                  <div style={labelStyle}>SMA 20 / 50</div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{info?.sma_20 ?? '—'} / {info?.sma_50 ?? '—'}</div>
                </div>
                <div>
                  <div style={labelStyle}>52W Range</div>
                  <div style={{ fontWeight: 600, fontSize: 15 }}>
                    {info?.['52w_low'] != null && info?.['52w_high'] != null ? `${info['52w_low']} - ${info['52w_high']}` : '—'}
                  </div>
                </div>
              </div>
            )}
          </section>

          <section style={sectionStyle}>
            <div style={{ ...labelStyle, marginBottom: 10 }}>Price Chart</div>
            {loading ? (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading chart...</div>
            ) : priceChartData.length > 0 ? (
              // key must not change until `data` (and priceChartData) has already been updated to
              // match — PriceChart only reads its `data` prop once, on mount (see its own comments),
              // so remounting it before a period/asset-type change's fetch has resolved would freeze
              // the chart on stale data forever. Gating this whole branch on `!loading` guarantees
              // `data` is already fresh by the time this key (re)mounts a new chart instance.
              <PriceChart key={`${ticker}-${resolvedAssetType}-${period}`} data={priceChartData} indicatorNames={[]} tradeEvents={[]} isFx={resolvedAssetType === 'fx'} />
            ) : (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>No price data available.</div>
            )}
          </section>

          {resolvedAssetType === 'stock' && data.earnings && !data.earnings.error && (
            <EarningsFundamentalsCard data={data.earnings} />
          )}

          {resolvedAssetType === 'stock' && data.sec && !data.sec.error && (
            <section style={sectionStyle}>
              <div style={{ ...labelStyle, marginBottom: 10 }}>SEC Filings &amp; Insider Trades</div>
              {Array.isArray(data.sec.recent_filings) && data.sec.recent_filings.length > 0 ? (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Recent Filings</div>
                  {data.sec.recent_filings.slice(0, 6).map((f, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: 12 }}>
                      <span>{f.form} &mdash; {f.description || f.form}</span>
                      <span style={{ color: 'var(--muted)' }}>{f.filing_date}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 14 }}>No recent filings found.</div>
              )}

              {Array.isArray(data.sec.insider_trades) && data.sec.insider_trades.length > 0 ? (
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Insider Trades</div>
                  {data.sec.insider_trades.slice(0, 6).map((t, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: 12 }}>
                      <span>{t.filer} &mdash; {t.transaction_type}</span>
                      <span style={{ color: 'var(--muted)' }}>{t.date}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--muted)', fontSize: 12 }}>No recent insider trades found.</div>
              )}
            </section>
          )}

          <section style={sectionStyle}>
            <div style={{ ...labelStyle, marginBottom: 10 }}>Recent News</div>
            {data.news?.articles && !data.news.error && data.news.articles.length > 0 ? (
              data.news.articles.slice(0, 6).map((item) => (
                <div
                  key={item.url}
                  onClick={() => { if (item.url) window.open(item.url, '_blank', 'noopener,noreferrer'); }}
                  style={{ padding: '10px 0', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', gap: 10, cursor: item.url ? 'pointer' : 'default' }}
                >
                  <div style={{ fontSize: 13 }}>{item.title}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 11, whiteSpace: 'nowrap' }}>{relativeAge(item.published_at)}</div>
                </div>
              ))
            ) : (
              <div style={{ color: 'var(--muted)', fontSize: 13 }}>No recent news found.</div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
